import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { GuidePeekCard } from './GuidePeekCard';
import { GUIDE_CARD_ROW_WIDTH_CSS } from './guideCardWidth';
import type { GuideBrowseListing } from './guideListings';
import { guidePeekImageUrls } from './publishedGuides';
import { gaGuideCardClick, gaGuideSeeAllClick, gaOccasionCardClick } from '../lib/gaPixel';
import { HubGuideCollageCard } from '../hub/HubGuideCollageCard';
import { PAGE_GUTTER_DESKTOP } from '../lib/pageGutter';

/**
 * What the rail needs to know about one card's guide: the registry listing
 * shape, plus the published index's optional inline peek art.
 *
 * Every production caller already hands this rail `PublishedGuideListing`s
 * read from Firestore, which carry `previewImages`; the prop type said
 * `GuideBrowseListing`, which does not, so that field was structurally
 * dropped on the way in and the rail could only ever see the materialized
 * map (THE-492). Naming it here is what lets guidePeekImageUrls apply the
 * same precedence /gift-guides applies, without the rail depending on the
 * published-index type.
 */
export type GuidePeekRailListing = GuideBrowseListing & {
  /** Inline peek art from the published index; wins over the materialized
   *  map. See guidePeekImageUrls. */
  previewImages?: string[];
};

/**
 * The guide rail's SCROLLER — the track, the desktop paging arrows, the header
 * row, and the card sizing — with nothing in it about which guides to show.
 *
 * Lifted verbatim out of HomeGuideRail when the board rail (SPEC §6a) needed
 * the same thing. Thea parameterizes one shared component rather than forking,
 * so the two surfaces cannot drift: an arrow fix, a snap change, or a width
 * formula lands on the homepage and the board at once. HomeGuideRail is now a
 * thin wrapper that decides its listings (in-season guides mixed into the season-
 * filtered registry) and its copy; BoardGuideRail decides its own (the matched
 * keys stored on the recommendation). Everything visual lives here.
 *
 * Callers own the DATA (listings + preview images) and the COPY (heading,
 * href). This component owns only what the rail IS.
 *
 * The comments below are the originals from HomeGuideRail — they encode why
 * the right padding is load-bearing (the card sizing itself now comes from
 * THE-876's shared `GUIDE_CARD_ROW_WIDTH_CSS`, not a per-rail formula). They
 * move with the code they explain.
 */

// Matches TestimonialsCarousel's OuterWrap so the two sections stack as one
// page rhythm rather than two separately-designed blocks. `className` is
// forwarded so a surface with different vertical rhythm (the board feed) can
// restyle the box with styled(GuidePeekRail) without forking the rail.
// `$wideGutter`: THE-1161 opt-in (see `wideGutter` on GuidePeekRailProps).
// Only HomeGuideRail, under the hub frame, passes it — every other caller
// (the board rail, /gift-guides' use of this rail) keeps the flat
// 1280px-cap + 64px-gutter treatment below byte-identically, which is why
// the override lives in its own >=1024px-only, opt-in block rather than
// replacing the base rule.
const OuterWrap = styled.section<{ $wideGutter?: boolean }>`
  max-width: 1280px;
  margin: 0 auto;
  padding: 64px 0 0;
  @media (min-width: 768px) {
    padding: 88px 0 0;
  }
  @media (min-width: 1024px) {
    ${({ $wideGutter }) =>
      $wideGutter &&
      css`
        max-width: none;
        margin: 0;
      `}
  }
`;

// The header row carries the SAME horizontal padding as the track below it, so
// the heading's left edge and the first card's left edge line up exactly.
//
// --peek-rail-gutter: a page that puts this rail inside its own gutter grid
// (the V1 board's "Dive deeper" band, whose plum runs edge to edge while its
// heading and first card start on the board's gutter) sets one inset for
// every width. Unset — everywhere else — each breakpoint keeps its own value.
const railGutter = (fallbackPx: number) =>
  `var(--peek-rail-gutter, ${fallbackPx}px)`;

const HeaderRow = styled.div<{ $compact?: boolean; $wideGutter?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 0 16px;
  padding-left: ${railGutter(16)};
  padding-right: ${railGutter(16)};
  margin: ${({ $compact }) => ($compact ? '0 0 14px' : '0 0 32px')};
  @media (min-width: 640px) {
    padding: 0 32px;
    padding-left: ${railGutter(32)};
    padding-right: ${railGutter(32)};
  }
  @media (min-width: 768px) {
    margin: ${({ $compact }) => ($compact ? '0 0 16px' : '0 0 48px')};
  }
  @media (min-width: 1024px) {
    padding: 0 64px;
    padding-left: ${railGutter(64)};
    padding-right: ${railGutter(64)};
    /* THE-1161 opt-in ($wideGutter, see GuidePeekRailProps): switches the
       flat 64px fallback above to the shared, viewport-growing
       PAGE_GUTTER_DESKTOP formula. Declared after the plain rule, in the
       same block, so it wins by source order without needing !important —
       every other caller leaves $wideGutter unset and renders the line
       above byte-identically. */
    ${({ $wideGutter }) =>
      $wideGutter &&
      css`
        padding-left: ${PAGE_GUTTER_DESKTOP};
        padding-right: ${PAGE_GUTTER_DESKTOP};
      `}
  }
`;

// Identical register to TestimonialsCarousel's Heading (28 → 40px, 700), minus
// its centering: this one shares its row with "See all".
//
// THE-1081: `$matchHub` swaps the 28/40px marketing display size for the
// gift-guides hub's own 26/28px shared heading register (hubType.ts
// HUB_HEADING_CSS) — "the section title the hub's headings already use."
// Opt-in so every other caller (the board rail, /gift-guides) renders
// byte-identically.
const Heading = styled.h2<{ $invertText?: boolean; $matchHub?: boolean }>`
  font-size: ${({ $matchHub }) => ($matchHub ? '26px' : '28px')};
  font-weight: 700;
  letter-spacing: -0.025em;
  color: ${({ $invertText }) => ($invertText ? '#FFFFFF' : 'hsl(var(--foreground))')};
  margin: 0;
  line-height: 1.1;
  @media (min-width: 768px) {
    font-size: ${({ $matchHub }) => ($matchHub ? '28px' : '40px')};
  }
`;

/* `compact`: a band INSIDE a page section (the board's "Dive deeper into X",
   which sits under the section's own 22px heading) — a subordinate heading,
   so smaller than the heading it belongs to. THE-1232: reads
   `theme.text.tile` (15/17, the board's "tile titles" role — same register
   BoardProgressCard's title uses) + `theme.weight.semibold` instead of a
   literal 17px/700 ("nothing bold-bold" board-wide), stepping down on phone
   the same way the band's own card titles do (HubGuideCollageCard's `$fit`
   Title) rather than pinning the desktop size at every width — QA round 1
   (2026-10-27) caught the earlier single-size version sitting a step above
   the 15px card titles it sits beside on phone. */
const CompactHeading = styled.h3<{ $invertText?: boolean }>`
  font-size: ${({ theme }) => theme.text.tile.mobile};
  font-weight: ${({ theme }) => theme.weight.semibold};
  letter-spacing: -0.01em;
  line-height: 1.3;
  color: ${({ $invertText }) => ($invertText ? '#FFFFFF' : 'hsl(var(--foreground))')};
  margin: 0;
  @media (min-width: 768px) {
    font-size: ${({ theme }) => theme.text.tile.desktop};
  }
`;

/* The whole heading is the primary door to /gift-guides — a 28/40px target
   next to "See all"'s 15/17px one, which is most of the tappable area of the
   header row on a phone. Inherits the heading's colour and weight so it does
   not read as a second, competing link style; the underline only appears on
   hover. Both doors fire the same event (see the onClick). */
const HeadingLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const SeeAll = styled(Link)`
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.clay};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 3px;
    border-radius: 4px;
  }
  @media (min-width: 768px) {
    font-size: 17px;
  }
`;

// Anchors the absolutely-positioned desktop arrows over the track.
const RailViewport = styled.div`
  position: relative;
`;

// Flush-left at the section padding — TestimonialsCarousel's Track, mirrored
// exactly, including its SYMMETRIC horizontal padding (16 → 32 @640 → 64
// @1024).
//
// RailCard is a FIXED width now (THE-876), not a percentage of this track's
// content box, so the padding below no longer sets a calc() basis — it is
// ordinary symmetric inset. It still matters for where the peeking card's
// bleed starts: a scroll container's overflow region includes its end
// padding, so the peeking card paints straight through the right padding and
// on to the physical viewport edge. That is expected and is the "there is
// more this way" bleed.
const Track = styled.div<{ $collage?: boolean; $fitTitles?: boolean; $wideGutter?: boolean }>`
  display: flex;
  /* THE-870: the collage card is its own white, shadowed box (no shared strip
     baseline to stretch to, unlike GuidePeekCard's alignStripToBottom) — the
     default flex stretch would otherwise inflate every card to the row's
     tallest and leave blank space under its title. With fitted titles
     (compact) a card's height follows its own title, so the row stretches
     back to its tallest card: no ragged white bottoms, and a row of one-line
     titles has no blank line at all. */
  align-items: ${({ $collage, $fitTitles }) =>
    $collage && !$fitTitles ? 'flex-start' : 'stretch'};
  gap: 16px;
  overflow-x: auto;
  /* Owns its horizontal axis -- THE-803 [PLAT]: every nested scroller
     declares overscroll-behavior on the axis it owns. The payoff is Android
     Chrome and laptop trackpads, where a flick running off the end chains
     into the document's horizontal axis and drives back/forward. NOT an iOS
     fix: measured on device 2026-10-05, this declaration makes no difference
     to iOS Safari's back gesture, which sits above the page out of CSS's
     reach. Latent today: THE-805 relaxed only the root's VERTICAL axis, so
     the root still carries overscroll-behavior-x: none as an outer belt and
     nothing chains here yet. This is the inner belt, gated so it cannot be
     dropped before it is the only one. Rationale and sweep:
     src/__tests__/railOverscrollContainment.test.ts. */
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-padding-left: ${railGutter(16)};
  -webkit-overflow-scrolling: touch;
  padding: 0 16px 8px;
  padding-left: ${railGutter(16)};
  padding-right: ${railGutter(16)};
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (min-width: 640px) {
    padding-left: ${railGutter(32)};
    padding-right: ${railGutter(32)};
    scroll-padding-left: ${railGutter(32)};
  }
  @media (min-width: 768px) {
    gap: 24px;
  }
  @media (min-width: 1024px) {
    padding-left: ${railGutter(64)};
    padding-right: ${railGutter(64)};
    scroll-padding-left: ${railGutter(64)};
    /* THE-1161 opt-in ($wideGutter, see GuidePeekRailProps) — same override
       as HeaderRow's, and for the same reason: the header's left edge and
       the first card's left edge have to keep lining up once either one
       switches off the flat 64px fallback. */
    ${({ $wideGutter }) =>
      $wideGutter &&
      css`
        padding-left: ${PAGE_GUTTER_DESKTOP};
        padding-right: ${PAGE_GUTTER_DESKTOP};
        scroll-padding-left: ${PAGE_GUTTER_DESKTOP};
      `}
  }
`;

/* THE-876: the shared standard guide-card width (384px desktop, 69vw peek on
   phone) replaces this rail's own "N full cards + 20% of the next" percentage
   formula. That formula was deliberately a percentage of the TRACK's own
   content box so the card count could step (1 → ~1.5 → 2 full) across the
   mobile/tablet/desktop bands; the standard is simpler by design — ONE card
   size everywhere, the same number this rail showed at its old 1024px step
   (386px), so the rail does not visibly jump on landing. GUIDE_CARD_TYPE_BREAKPOINT_PX
   no longer needs a matching width step: the title's own 2-line clamp
   (GUIDE_CARD_TITLE_CLAMP_CSS) now reserves identical height on both sides of
   that band, so a type bump at a fixed card width cannot reintroduce THE-183's
   misaligned strips. */
const RailCard = styled(GuidePeekCard)`
  ${GUIDE_CARD_ROW_WIDTH_CSS}
  scroll-snap-align: start;
`;

/* THE-870: same width as RailCard, carrying the hub's "More gift guides"
   card (name under the photo, white background + drop shadow) instead of the
   plain GuidePeekCard — reused as-is, not forked, so a restyle of that card
   lands on both rows at once. Opt-in via `useCollageCard` so the board rail
   and /gift-guides keep today's card. */
const RailCollageCard = styled(HubGuideCollageCard)`
  ${GUIDE_CARD_ROW_WIDTH_CSS}
  scroll-snap-align: start;
`;

/* Desktop-only page arrow, rendered ONLY when the rail can scroll that way so
   it vanishes at each end (ProductDetailSheet's "More like this" pattern).
   A real <button> with an aria-label, not a clickable glyph: keyboard and
   screen-reader users get the same control the mouse gets. Touch surfaces
   never render it — swipe is the native affordance there. Vertically centered
   on the track, which lands it inside the photo strip band. */
const RailArrow = styled.button<{ $dir: 'prev' | 'next' }>`
  position: absolute;
  top: 50%;
  ${({ $dir }) => ($dir === 'prev' ? 'left: 16px;' : 'right: 16px;')}
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: hsla(0, 0%, 100%, 0.94);
  color: ${({ theme }) => theme.color.inkSoft};
  cursor: pointer;
  box-shadow: 0 2px 10px hsla(28, 40%, 40%, 0.2);
  transition:
    background var(--dur-press) ease,
    box-shadow var(--dur-press) ease;
  &:hover {
    background: hsl(0, 0%, 100%);
    box-shadow: 0 3px 13px hsla(28, 40%, 40%, 0.26);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

/* "5 guides" (THE-950) — sits under the next-arrow, same gating (desktop +
   canScrollRight) so it never appears with nothing left to page to. Plain
   text, not a button: it names what's beyond the fold, it doesn't act on
   anything the arrow doesn't already. */
/* Hangs 28px below the track — which needs that much band below the cards.
   The compact band doesn't have it (Kate 2026-10-14: tighter padding), so
   there it sits in the header row instead, across from the heading
   ($inHeader — still desktop + overflow only, same gate). */
const OverflowCount = styled.p<{ $invertText?: boolean; $inHeader?: boolean }>`
  position: absolute;
  bottom: -28px;
  right: 16px;
  margin: 0;
  ${({ $inHeader }) =>
    $inHeader &&
    `
      position: static;
      flex: 0 0 auto;
      white-space: nowrap;
    `}
  /* THE-1232: "5 guides" reads theme.text.small.desktop (14px, replacing a
     standalone 13px literal — the same "small" role AffiliateDisclosure's
     "notes" line already migrated to) plus theme.weight.semibold. The ink
     branch reads theme.color.mutedForeground, not the banned inkSoft. */
  font-size: ${({ theme }) => theme.text.small.desktop};
  font-weight: ${({ theme }) => theme.weight.semibold};
  color: ${({ theme, $invertText }) => ($invertText ? '#FFFFFF' : theme.color.mutedForeground)};
`;

const Chevron: React.FC<{ dir: 'prev' | 'next' }> = ({ dir }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <polyline points={dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
  </svg>
);

export interface GuidePeekRailProps {
  /** Exactly the cards to render, in order. Empty → the rail renders NOTHING
   *  (no heading, no empty track) on every surface. */
  listings: GuidePeekRailListing[];
  /** Listing key → peek-strip image urls, from the MATERIALIZED previews doc.
   *  Callers hydrate (useGuidePreviewDoc) or inject (stories/tests). Consulted
   *  only for listings with no inline `previewImages`; a listing covered by
   *  neither pads with placeholder tiles. */
  previewImages: Record<string, string[]>;
  headingText: string;
  /** Where the heading and "See all →" point. */
  headingHref: string;
  /** DOM id for the <h2>, referenced by the section's aria-labelledby. Two
   *  rails on one page would otherwise collide on a hardcoded id. */
  headingId: string;
  /** Accessible names for the paging buttons, e.g. "Previous gift guides". */
  prevLabel: string;
  nextLabel: string;
  /**
   * GA `entry_point` for clicks originating here. OMITTED = analytics off —
   * that is the board rail's deliberate v1 posture (SPEC §7: seam only, a
   * later `entry_point: 'board_rail'` is one line). Absent means NO event is
   * fired, not an event with an empty entry_point.
   */
  entryPoint?: string;
  /** Per-card react-router `state` for the link — the board rail's origin
   *  descriptor. Omitted on surfaces that pass no state (the homepage). */
  linkStateFor?: (listing: GuidePeekRailListing) => unknown;
  /** Per-card href override — the board rail carries the cohort query param
   *  onto card links. Defaults to the listing's own href. */
  hrefFor?: (listing: GuidePeekRailListing) => string;
  /**
   * Drop the "See all →" link from the header row (THE-729 item #2, board
   * 2026-10-01: *"i want to remove the 'see all' with an arrow on the top right
   * of the Gift guides section"*). Board rail only — the marketing homepage and
   * the hub keep it.
   *
   * The HEADING stays a link to the same place either way, so removing this
   * closes no door: it removes the smaller of two doors to /gift-guides that sat
   * in one row. Opt-IN (default false) so the surfaces that were not asked about
   * render byte-identically to before.
   */
  hideSeeAll?: boolean;
  /**
   * Render each card's title BELOW its photo strip instead of above it
   * (THE-729 item #3, board 2026-10-01: *"i want to move the title of each gift
   * guide underneath each gift guide image preview, instead of on top (where it
   * is today). only make this change on the recipient boards — all other gift
   * guide pages stay the same"*).
   *
   * Forwarded to GuidePeekCard, which owns what the reorder actually means for
   * the strip baseline. Opt-in for the same reason as `hideSeeAll`.
   */
  titleBelow?: boolean;
  /**
   * Render each card as the hub's `HubGuideCollageCard` (name under the
   * photo, white card + drop shadow) instead of the plain `GuidePeekCard`
   * (THE-870: "Trending gift guides" should read as one system with "More
   * gift guides"). Opt-in, and ignores `titleBelow`/`alignStripToBottom`
   * (the collage card is always title-under-image and never stretched —
   * see the Track comment). Only HomeGuideRail passes it; the board rail
   * and /gift-guides keep the plain card.
   */
  useCollageCard?: boolean;
  /** Lets a caller's styled() wrapper retune the section box (board rhythm). */
  className?: string;
  /**
   * Runs alongside (not instead of) the GA click handler above, and — unlike
   * it — always runs, regardless of `entryPoint`. The board's per-section
   * "Dive deeper" rail (THE-950) uses this to open a card's guide as a
   * sheet/pop-up over the board on a plain click, while a modified click
   * (⌘/Ctrl/middle-click) is left to open the real `href` in a new tab as
   * normal — the callback decides which by inspecting the event, not this
   * component. Omitted everywhere else, so every other rail's cards keep
   * navigating exactly as before.
   */
  onCardClick?: (listing: GuidePeekRailListing, event: React.MouseEvent) => void;
  /**
   * Desktop-only overflow count shown beside the next-page arrow (THE-950
   * QA: "that overflow case also gets a round › button and a count ('5
   * guides')"). Rendered ONLY when the arrow itself would render (desktop +
   * `canScrollRight`) — passing a number doesn't force it to show on a rail
   * that already fits everything. Omitted on every surface that doesn't ask
   * for it (home rail, hub, /gift-guides, the old board rail).
   */
  overflowCount?: number;
  /**
   * Flips the heading and overflow-count text to white (THE-950's plum "Dive
   * deeper" block is the first caller with a dark background — every other
   * surface sits on cream/white and leaves this unset). `SeeAll` needs no
   * matching flip: callers this dark always pass `hideSeeAll`.
   */
  invertText?: boolean;
  /**
   * The rail as a band WITHIN a page section rather than a section of its own
   * (the board's per-section "Dive deeper" block — Kate 2026-10-14: its
   * heading was bigger than the board's own section headings and the band
   * took too much height). Renders the heading as an <h3> at the board's
   * 17px secondary-heading size, tightens the heading-to-cards gap, and lets
   * collage cards end right after their title instead of reserving a second,
   * usually-empty title line (see HubGuideCollageCard's `fitTitle`). Opt-in:
   * every other surface renders byte-identically.
   */
  compact?: boolean;
  /**
   * THE-1081: size the heading to the gift-guides hub's own 26/28px shared
   * register instead of the 28/40px marketing display size — "every section
   * title the same size," matching the hub's headings. Only the homepage
   * passes this; every other caller (the board rail, /gift-guides) renders
   * byte-identically.
   */
  matchHubHeading?: boolean;
  /**
   * THE-1161: switches the rail's own default gutter (OuterWrap's
   * `max-width: 1280px; margin: 0 auto` cap, HeaderRow/Track's flat 64px
   * fallback) onto the shared, viewport-growing `PAGE_GUTTER_DESKTOP`
   * formula at >=1024px only. Default unset so every other caller — the
   * board rail (BoardGuideRail/BoardRail) and the plain /gift-guides use of
   * this rail — renders byte-identically; only HomeGuideRail passes it, and
   * only under the hub frame (see that file's own comment on why: the SAME
   * homepage route's Coming Up strip and nav already read this formula once
   * the frame is active, so leaving this rail on its old fixed values would
   * give one page two different left edges). Not the `--peek-rail-gutter`
   * CSS var mechanism above — that is for a page that wants ONE flat inset
   * at every width; this is specifically "switch to the viewport-dependent
   * formula at >=1024px."
   */
  wideGutter?: boolean;
}

export const GuidePeekRail: React.FC<GuidePeekRailProps> = ({
  listings,
  previewImages,
  headingText,
  headingHref,
  headingId,
  prevLabel,
  nextLabel,
  entryPoint,
  linkStateFor,
  hrefFor,
  hideSeeAll = false,
  titleBelow = false,
  useCollageCard = false,
  className,
  onCardClick,
  overflowCount,
  invertText = false,
  compact = false,
  matchHubHeading = false,
  wideGutter = false,
}) => {
  const railRef = useRef<HTMLDivElement | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Arrows are gated on a real viewport check, not CSS alone: a hidden button
  // is still focusable and still announced, so mobile users would tab into
  // controls they cannot see.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    // Safari <14 only has the deprecated listener API.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  const updateBounds = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // 1px slack absorbs sub-pixel scroll positions, which would otherwise
    // leave an arrow rendered at a hard end.
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < max - 1);
  }, []);

  useEffect(() => {
    updateBounds();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [updateBounds, listings.length]);

  const pageRail = (dir: 1 | -1) => {
    const el = railRef.current;
    if (!el) return;
    // Smooth by default, instant for reduced-motion users and under
    // automation — so nobody is animated at against their setting and Happo
    // never photographs a mid-scroll frame. Same gate ProductDetailSheet uses.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const smooth =
      !prefersReducedMotion &&
      !(typeof navigator !== 'undefined' && navigator.webdriver);
    el.scrollBy({
      left: dir * Math.max(el.clientWidth * 0.9, 200),
      behavior: smooth ? 'smooth' : 'auto',
    });
  };

  // Fires the surface's click event, or nothing at all when the surface opted
  // out of analytics. Returning `undefined` (not a no-op function) keeps the
  // card's onClick prop genuinely absent on an analytics-off surface.
  const clickHandlerFor = (listing: GuideBrowseListing): (() => void) | undefined => {
    if (entryPoint === undefined) return undefined;
    return listing.kind === 'guide'
      ? () =>
          gaGuideCardClick({
            occasion: listing.key,
            surface: 'gift_guide',
            entry_point: entryPoint,
          })
      : () => gaOccasionCardClick({ occasion: listing.key, entry_point: entryPoint });
  };

  const onSeeAllClick =
    entryPoint === undefined
      ? undefined
      : () => gaGuideSeeAllClick({ entry_point: entryPoint });

  // Composes the GA handler above with `onCardClick`, so a surface that wants
  // both (unlikely — `onCardClick` callers today skip `entryPoint`) gets
  // both, and a surface with neither keeps its card's onClick genuinely
  // absent rather than a no-op function.
  const composedOnClick = (listing: GuideBrowseListing): ((event: React.MouseEvent) => void) | undefined => {
    const ga = clickHandlerFor(listing);
    if (!ga && !onCardClick) return undefined;
    return (event) => {
      ga?.();
      onCardClick?.(listing, event);
    };
  };

  if (listings.length === 0) return null;

  const showOverflowCount = isDesktop && canScrollRight && overflowCount !== undefined;
  const overflowCountText =
    overflowCount === undefined ? '' : `${overflowCount} ${overflowCount === 1 ? 'guide' : 'guides'}`;

  return (
    <OuterWrap aria-labelledby={headingId} className={className} $wideGutter={wideGutter}>
      <HeaderRow $compact={compact} $wideGutter={wideGutter}>
        {/* id stays on the heading, not the link, so the section's
            aria-labelledby still resolves to the heading itself. */}
        {compact ? (
          <CompactHeading id={headingId} $invertText={invertText}>
            <HeadingLink to={headingHref} onClick={onSeeAllClick}>
              {headingText}
            </HeadingLink>
          </CompactHeading>
        ) : (
          <Heading id={headingId} $invertText={invertText} $matchHub={matchHubHeading}>
            <HeadingLink to={headingHref} onClick={onSeeAllClick}>
              {headingText}
            </HeadingLink>
          </Heading>
        )}
        {!hideSeeAll && (
          <SeeAll to={headingHref} onClick={onSeeAllClick}>
            See all →
          </SeeAll>
        )}
        {compact && showOverflowCount && (
          <OverflowCount $invertText={invertText} $inHeader>
            {overflowCountText}
          </OverflowCount>
        )}
      </HeaderRow>
      <RailViewport>
        {isDesktop && canScrollLeft && (
          <RailArrow
            type="button"
            $dir="prev"
            aria-label={prevLabel}
            onClick={() => pageRail(-1)}
          >
            <Chevron dir="prev" />
          </RailArrow>
        )}
        <Track
          $collage={useCollageCard}
          $fitTitles={compact && useCollageCard}
          $wideGutter={wideGutter}
          ref={railRef}
          onScroll={updateBounds}
        >
          {listings.map((listing) =>
            useCollageCard ? (
              <RailCollageCard
                key={listing.key}
                href={hrefFor ? hrefFor(listing) : listing.href}
                title={listing.title}
                emoji={listing.emoji}
                imageUrls={guidePeekImageUrls(listing, previewImages)}
                fitTitle={compact}
                linkState={linkStateFor ? linkStateFor(listing) : undefined}
                onClick={composedOnClick(listing)}
              />
            ) : (
              <RailCard
                key={listing.key}
                href={hrefFor ? hrefFor(listing) : listing.href}
                title={listing.title}
                emoji={listing.emoji}
                imageUrls={guidePeekImageUrls(listing, previewImages)}
                titleBelow={titleBelow}
                alignStripToBottom
                linkState={linkStateFor ? linkStateFor(listing) : undefined}
                onClick={composedOnClick(listing)}
              />
            ),
          )}
        </Track>
        {isDesktop && canScrollRight && (
          <RailArrow
            type="button"
            $dir="next"
            aria-label={nextLabel}
            onClick={() => pageRail(1)}
          >
            <Chevron dir="next" />
          </RailArrow>
        )}
        {!compact && showOverflowCount && (
          <OverflowCount $invertText={invertText}>{overflowCountText}</OverflowCount>
        )}
      </RailViewport>
    </OuterWrap>
  );
};

export default GuidePeekRail;
